"use client";

import { Danger } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { formatGwei } from "viem";
import { useGasPrice } from "wagmi";
import { XIcon } from "@/components/ui";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HIGH_GAS_THRESHOLD_GWEI } from "@/hooks/useGasPriceCheck";
import { formatNumber } from "@/lib/utils/number-utils";

interface HighGasPriceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceed: () => void;
  onCancel: () => void;
  thresholdGwei?: string;
  isLoading?: boolean;
}

export const HighGasPriceModal = ({
  open,
  onOpenChange,
  onProceed,
  onCancel,
  thresholdGwei,
  isLoading = false,
}: HighGasPriceModalProps) => {
  const t = useTranslations("modals");
  const { data: gasPrice, isLoading: isFetchingGas, isError } = useGasPrice();

  const currentGasPriceGwei = gasPrice ? formatGwei(gasPrice) : "0";
  const displayThreshold = thresholdGwei ?? HIGH_GAS_THRESHOLD_GWEI.toString();
  const currentFee =
    isFetchingGas || isError ? "..." : formatNumber(currentGasPriceGwei);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs space-y-4 px-9">
        <DialogHeader className="w-full">
          <DialogTitle className="flex items-center">
            <div className="flex items-center justify-center gap-3 w-full">
              <Danger
                size={24}
                variant="Bold"
                className="text-semantic-alert"
              />
              <span className="text-lg font-semibold text-semantic-alert mt-1.5">
                {t("highGasPrice.warning")}
              </span>
            </div>
            <DialogClose asChild onClick={onCancel}>
              <XIcon size={24} className="text-text-disabled" />
            </DialogClose>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-center text-sm text-text-primary font-normal">
          {t("highGasPrice.message")}
        </DialogDescription>
        {/* DialogFooter defaults to `sm:flex-row sm:justify-end sm:space-x-2`.
            An unprefixed `flex-col` does NOT cancel a `sm:`-prefixed class in
            tailwind-merge, so on desktop this footer used to flip to a row:
            the w-full button ate the whole track and the fee line was squeezed
            into a one-word-per-line column. Override at the sm: breakpoint. */}
        <DialogFooter className="flex flex-col gap-3 w-full sm:flex-col sm:space-x-0">
          <Button
            className="w-full"
            variant="solid"
            colors="secondary"
            onClick={onProceed}
            loading={isLoading}
          >
            {t("highGasPrice.proceedButton")}
          </Button>
          <p className="text-muted text-center text-xs">
            {t("highGasPrice.feeComparison", {
              displayThreshold,
              current: currentFee,
            })}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
